import React, { useEffect, useRef, useState, useMemo } from "react";
import promoImage from "../../../../assets/img/promocode/coupon.png";
import { HiFilter, HiPlus, HiSearch } from "react-icons/hi";
import { FaEdit } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";
import PromoWarningPopup from "./PromoWarningPopup";
import PromoEditPopup from "./PromoEditPopup";
import CoinPromoEdit from "./CoinPromoEdit";
import CoinPromoWarning from "./CoinPromoWarning";
import { usePermission } from "Context/PermissionContext";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const ListOfCoinPromoCode = ({
  promos,
  userDataLoading,
  handleAddModalOpen,
  fetchPromos,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("All Promo");
  const [searchTerm, setSearchTerm] = useState("");
  const [warnPopupOpen, setWarnPopupOpen] = useState(false);
  const [promoDetails, setPromoDetails] = useState([]);
  const [editPromoModal, setEditPromoModal] = useState(false);
  const dropdownRef = useRef(null);
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.coinManagement?.subModule?.coinPromocode?.permissions;

  const loggedInUser = loadDecrypted("HeartConnect-StaffData");
  const staffData = Array.isArray(loggedInUser)
    ? loggedInUser[0]
    : loggedInUser;
  const staffId = staffData?._id || staffData?.id || "";
  // console.log("getAllPromos", promos);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredPromos = useMemo(() => {
    let filtered = promos;
    if (selectedOption === "Seasonal Promo") {
      filtered = promos.filter((promo) => promo.promoType === "seasonal");
    } else if (selectedOption === "Special Promo") {
      filtered = promos.filter((promo) => promo.promoType === "special");
    } else if (selectedOption === "Hourly Promo") {
      filtered = promos.filter((promo) => promo.promoType === "hour-basis");
    }
    if (searchTerm) {
      filtered = filtered.filter((promo) =>
        promo.promoCode.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [promos, selectedOption, searchTerm]);

  const gradientColors = [
    "from-pink-500 to-purple-500",
    "from-green-400 to-blue-500",
    "from-yellow-400 to-orange-500",
    "from-red-400 to-pink-500",
    "from-indigo-400 to-purple-500",
    "from-blue-400 to-teal-500",
  ];

  const handleOpenDeleteModal = (item) => {
    setWarnPopupOpen(true);
    setPromoDetails(item);
  };

  const handleClose = () => {
    setWarnPopupOpen(false);
  };

  const handelEditModalOpen = (item) => {
    setEditPromoModal(true);
    setPromoDetails(item);
  };

  const handleEditModalClose = () => {
    setEditPromoModal(false);
  };

  return (
    <React.Fragment>
      <CoinPromoWarning
        fetchPromos={fetchPromos}
        PromoDetails={promoDetails}
        warnPopupOpen={warnPopupOpen}
        handleClose={handleClose}
        staffId={staffId}
      />

      <CoinPromoEdit
        PromoDetails={promoDetails}
        editPromoModal={editPromoModal}
        handleEditModalClose={handleEditModalClose}
        fetchPromos={fetchPromos}
        staffId={staffId}
      />

      <div className="py-5">
        <div className="flex flex-col items-center justify-between space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
          {/* Search Bar */}
          <div className="w-full md:mx-4 md:w-1/2 md:flex-1">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                <HiSearch color="#c51369" className="h-5 w-5" />
              </span>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full rounded-md border px-4 py-2 pl-10 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369] md:w-1/2"
              />
            </div>
          </div>

          {/* Plus Button */}
          {currentTabPermissions?.create && (
            <div className="w-full md:w-auto">
              <button
                onClick={handleAddModalOpen}
                className="flex w-full items-center justify-center rounded-full bg-[#c51369] p-3 text-white hover:bg-[#a10f5b] hover:shadow-lg focus:outline-none md:w-auto"
              >
                <HiPlus className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        <div className="mx-auto py-5">
          {userDataLoading ? (
            <SkeletonLoader />
          ) : filteredPromos.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredPromos.map((promo, index) => (
                <div
                  key={index}
                  className={`relative flex items-center justify-between bg-gradient-to-tr ${
                    gradientColors[index % gradientColors.length]
                  } rounded-2xl p-6 text-white shadow-lg`}
                >
                  {/* Icons Section */}
                  <div className="absolute right-5 top-3 flex space-x-3">
                    {currentTabPermissions?.edit && (
                      <button onClick={() => handelEditModalOpen(promo)}>
                        <FaEdit
                          size={20}
                          className="cursor-pointer text-white hover:text-[#c51369]"
                        />
                      </button>
                    )}
                    {currentTabPermissions?.delete && (
                      <button onClick={() => handleOpenDeleteModal(promo)}>
                        <FaTrash
                          size={18}
                          className="cursor-pointer text-white hover:text-[#c51369]"
                        />
                      </button>
                    )}
                  </div>

                  {/* Promo Details */}
                  <div>
                    <h3 className="mb-2 text-xl font-light tracking-wider text-gray-200">
                      {promo?.code}
                    </h3>
                    <p className="mb-4 text-2xl font-bold tracking-wide">
                      {promo?.discount}%
                    </p>
                    <p className="text-md capitalize tracking-wide">
                      using Limit: {promo?.usageLimit}
                    </p>
                  </div>

                  {/* Promo Image */}
                  <div>
                    <img
                      src={promoImage}
                      alt="Coupon"
                      className="h-14 w-14 rounded-lg"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-4 text-center tracking-wide text-gray-500">
              No data found
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default ListOfCoinPromoCode;

// Skeleton loader component
const SkeletonLoader = () => (
  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: 6 }).map((_, index) => (
      <div
        key={index}
        className="flex animate-pulse items-center justify-between gap-4 rounded-2xl bg-gray-200 p-6 shadow-lg"
      >
        <div className="w-full">
          <div className="mb-2 h-6 rounded bg-gray-300"></div>
          <div className="mb-4 h-4 rounded bg-gray-300"></div>
          <div className="mb-2 h-4 rounded bg-gray-300"></div>
        </div>
        <div className="h-14 w-14 rounded-lg bg-gray-300"></div>
      </div>
    ))}
  </div>
);
